import {
    getAppHref,
    getAppSectionEntryCategories,
    getAppSectionEntryReleases,
    kAppCategories,
    kPeopleApps,
} from "@/client/apps/AppDefinitions";
import type { AppSection } from "@/client/apps/AppDefinitions";
import {
    getLatestAvailableVersion,
    kSpliceGenCapabilities,
} from "@/client/apps/SpliceGenCapabilities";
import { isAppEvaluationOnly } from "@/client/apps/AppModels";
import { kDeployedApps } from "@/client/apps/DeployedApps";
import type { AppCategory, AppDefinition } from "@/client/core/Core.api";
import { createAccessGate } from "@/client/core/components/access-gate.component";
import { createEmbeddedLinkOpener } from "@/client/core/services/external-link.service";
import { isAppVisible, type AudienceState } from "@/client/core/services/audience";
import { audienceStateSignal } from "@/client/core/services/feature-flags.signal";
import { createPageChangeService } from "@/client/core/services/page-change.service";
import { createEl } from "@/client/utils/createEl";
import { hasWebAccount } from "@/client/core/services/web-auth.service";
import { getDisplayMode } from "@/client/utils/getDisplayMode";
import { createAudienceTag } from "./audience-tag.component";
import { createBadge } from "../design-system/components/badge.component";
import { mountHeaderBackground } from "./header-background";
import { createRndOnlyTag } from "./rnd-only-tag.component";
import * as styles from "./app-directory.css";

/** The Apps tab that shows every card, ahead of the category tabs. */
const kAllTab = "All";
type AppsTab = AppCategory | typeof kAllTab;

// Session-scoped, so coming back to home from an app keeps the tab the
// visitor left on, and a new visit starts on All.
const kAppsTabStorageKey = "labs-home-apps-tab";

const readStoredAppsTab = (): AppsTab => {
    try {
        const stored = window.sessionStorage.getItem(kAppsTabStorageKey);
        const match = kAppCategories.find((category) => category === stored);
        return match ?? kAllTab;
    } catch {
        // A partitioned iframe can refuse storage; the tab just starts on All.
        return kAllTab;
    }
};

const storeAppsTab = (tab: AppsTab): void => {
    try {
        window.sessionStorage.setItem(kAppsTabStorageKey, tab);
    } catch {
        // Not remembered across pages; the tab still works on this one.
    }
};

export const createAppDirectory = (): HTMLElement => {
    const pageChangeService = createPageChangeService();
    // Now, not when the access gate renders: it listens for the host's
    // one-time handshake, which can arrive before audiences resolve.
    const openEmbeddedLink = createEmbeddedLinkOpener();

    // Background host lives inside the header element and is absolutely positioned to fill it.
    const bgHost = createEl("div", styles.headerBgHost);

    const headerEl = createEl("header", styles.siteHeader, [
        bgHost,
        createEl("div", styles.siteHeaderLeft, [
            createEl("h1", styles.siteTitle, [
                createEl("span", styles.siteTitleText, "Backstage"),
                // On the website the tier sits in the account menu, under the
                // signed-in name. The embedded home has no account menu, and
                // neither does an ungated web build (no Auth0 client), so the
                // chip, and with it R&D's audience preview, stays beside the
                // title there.
                ...(getDisplayMode() === "web" && hasWebAccount() ? [] : [createAudienceTag()]),
            ]),
            createEl("p", styles.siteTagline, "Near-future creative R&D on Splice"),
        ]),
    ]);

    mountHeaderBackground(bgHost);

    /**
     * A card: the app it opens, and the sentence to print on it.
     *
     * The sentence is passed rather than read off the app, because a SpliceGen
     * card describes the capability while the app it resolves to describes one
     * release of it.
     */
    interface HomeCard {
        readonly app: AppDefinition;
        readonly description: string;
        /** The Apps tabs it sits under besides All; SpliceGen cards have none. */
        readonly categories?: readonly AppCategory[];
        /**
         * Whether the card carries the R&D tag. SpliceGen cards never do: the
         * viewer picks the model inside the app, so the tag lives in its header
         * and follows that choice.
         */
        readonly shouldShowEvaluationTag: boolean;
    }

    const createAppCardEl = ({
        app,
        description,
        shouldShowEvaluationTag,
    }: HomeCard): HTMLElement => {
        const href = getAppHref(app);
        return createEl("li", styles.heroListItem, [
            createEl(
                "a",
                styles.heroCard,
                [
                    createEl("div", styles.heroCardTop, [
                        // No model chip here. Which release a card opens on
                        // depends on the viewer's audience, and the app states
                        // it in its own header where it can also be changed.
                        createEl("div", styles.heroCardTitleRow, [
                            createEl("p", styles.heroCardTitle, app.title),
                        ]),
                        ...(app.author !== undefined
                            ? [createEl("p", styles.heroCardBy, `by ${app.author}`)]
                            : []),
                        createEl("p", styles.heroCardDescription, description),
                    ]),
                    ...(shouldShowEvaluationTag
                        ? [
                              createEl("div", styles.heroCardBottom, [
                                  // The card is a link, which is already the focus stop.
                                  createRndOnlyTag({
                                      size: "compact",
                                      tooltipPlacement: "above",
                                      isFocusable: false,
                                  }),
                              ]),
                          ]
                        : []),
                ],
                (el) => {
                    el.href = href;
                    el.addEventListener("click", () => {
                        pageChangeService.onPageChange(href);
                    });
                },
            ),
        ]);
    };

    const createSectionEl = (
        title: string,
        cards: readonly HomeCard[],
        badge?: string,
    ): HTMLElement | undefined => {
        if (cards.length === 0) {
            return undefined;
        }
        const titleRowChildren: HTMLElement[] = [createEl("h2", styles.sectionTitle, title)];
        if (badge !== undefined) {
            titleRowChildren.push(createBadge({ text: badge, variant: "warning", theme: "dark" }));
        }
        return createEl("section", styles.section, [
            createEl("div", styles.heroLabelRow, titleRowChildren),
            createEl("ul", styles.heroGrid, cards.map(createAppCardEl)),
        ]);
    };

    let selectedAppsTab = readStoredAppsTab();

    /**
     * The Apps section: the title, a row of tabs (All, then each category
     * with a card this viewer can open), and the grid of the selected tab's
     * cards. Switching tabs redraws only the grid, so the page keeps its
     * scroll position.
     */
    const createAppsSectionEl = (
        title: string,
        cards: readonly HomeCard[],
    ): HTMLElement | undefined => {
        if (cards.length === 0) {
            return undefined;
        }
        const tabs: readonly AppsTab[] = [
            kAllTab,
            ...kAppCategories.filter((category) =>
                cards.some((card) => card.categories?.includes(category) === true),
            ),
        ];
        // All plus one category would be two tabs showing the same cards.
        const hasTabs = tabs.length > 2;
        // The visitor's choice stays in `selectedAppsTab` even when this
        // audience has nothing under it: the audience can still widen (a
        // fallback published before LaunchDarkly answers, or an R&D preview),
        // and the tab should come back when it does.
        let shownTab: AppsTab =
            hasTabs && tabs.includes(selectedAppsTab) ? selectedAppsTab : kAllTab;

        const gridEl = createEl("ul", `${styles.heroGrid} ${styles.heroGridFullWidthOnPhone}`);
        // The panel wraps the list rather than being it, so the cards are
        // still announced as a list.
        const panelEl = createEl("div", "", [gridEl], (el) => {
            el.id = "home-apps-panel";
            el.setAttribute("role", "tabpanel");
        });

        const tabEls = tabs.map((tab, index) =>
            createEl("button", styles.appsTab, tab, (el) => {
                el.type = "button";
                el.id = `home-apps-tab-${index}`;
                el.setAttribute("role", "tab");
                el.setAttribute("aria-controls", panelEl.id);
                el.addEventListener("click", () => select(tab));
                el.addEventListener("keydown", (event) => onTabKeyDown(event, tab));
            }),
        );

        const update = (): void => {
            tabs.forEach((tab, index) => {
                const isSelected = tab === shownTab;
                const tabEl = tabEls[index];
                tabEl.setAttribute("aria-selected", String(isSelected));
                // Roving tabindex: Tab reaches the selected tab, arrows move between them.
                tabEl.tabIndex = isSelected ? 0 : -1;
                if (isSelected) {
                    panelEl.setAttribute("aria-labelledby", tabEl.id);
                }
            });
            gridEl.replaceChildren(
                ...cards
                    .filter(
                        (card) =>
                            shownTab === kAllTab || card.categories?.includes(shownTab) === true,
                    )
                    .map(createAppCardEl),
            );
        };

        const select = (tab: AppsTab): void => {
            if (tab === shownTab) {
                return;
            }
            shownTab = tab;
            selectedAppsTab = tab;
            storeAppsTab(tab);
            update();
        };

        const onTabKeyDown = (event: KeyboardEvent, tab: AppsTab): void => {
            const index = tabs.indexOf(tab);
            const nextIndex =
                event.key === "ArrowRight"
                    ? (index + 1) % tabs.length
                    : event.key === "ArrowLeft"
                      ? (index - 1 + tabs.length) % tabs.length
                      : event.key === "Home"
                        ? 0
                        : event.key === "End"
                          ? tabs.length - 1
                          : undefined;
            if (nextIndex === undefined) {
                return;
            }
            event.preventDefault();
            select(tabs[nextIndex]);
            tabEls[nextIndex].focus();
        };

        update();

        const titleRowEl = createEl("div", styles.heroLabelRow, [
            createEl("h2", styles.sectionTitle, title),
        ]);
        if (!hasTabs) {
            return createEl("section", styles.section, [titleRowEl, gridEl]);
        }
        return createEl("section", styles.section, [
            titleRowEl,
            createEl("div", styles.appsTabs, tabEls, (el) => {
                el.setAttribute("role", "tablist");
                el.setAttribute("aria-label", `${title} by type`);
            }),
            panelEl,
        ]);
    };

    /**
     * An authored app's card says what that app's own definition says. An
     * entry with several releases opens the newest one the viewer can see.
     */
    const toAuthoredCards = (
        section: AppSection,
        audienceState: Readonly<AudienceState>,
    ): HomeCard[] =>
        section.apps
            .map((entry): HomeCard | undefined => {
                const app = getAppSectionEntryReleases(entry).find(
                    (release) => kDeployedApps.has(release) && isAppVisible(release, audienceState),
                );
                return app === undefined
                    ? undefined
                    : {
                          app,
                          description: app.description,
                          categories: getAppSectionEntryCategories(entry),
                          // A card holding several releases (Fill + Fit) lets
                          // the viewer pick the model inside the app, so the
                          // header carries the tag, as for Splice Models cards.
                          shouldShowEvaluationTag:
                              getAppSectionEntryReleases(entry).length === 1 &&
                              isAppEvaluationOnly(app),
                      };
            })
            .filter((card): card is HomeCard => card !== undefined);

    const scrollArea = createEl("div", styles.scrollArea, []);

    const update = (audienceState: Readonly<AudienceState>): void => {
        scrollArea.replaceChildren();
        if (!audienceState.isReady) {
            // The login gate overlays this state for logged-out visitors;
            // for logged-in users audiences resolve within a few seconds.
            return;
        }
        // One card per capability, each resolved to the newest release this
        // viewer can open, so internal, Soundcheck and partners get a card where they
        // would otherwise get nothing. The sentence on it is the capability's,
        // not the resolved release's, so the card reads the same for everyone.
        const canOpen = (app: AppDefinition): boolean =>
            kDeployedApps.has(app) && isAppVisible(app, audienceState);
        const spliceGenCards = kSpliceGenCapabilities
            .map((capability) => {
                const app = getLatestAvailableVersion(capability, canOpen);
                return app === undefined
                    ? undefined
                    : {
                          app,
                          description: capability.description,
                          shouldShowEvaluationTag: false,
                      };
            })
            .filter((card): card is HomeCard => card !== undefined);
        const spliceGenSection = createSectionEl("Splice Models", spliceGenCards);
        const peopleSection = createAppsSectionEl(
            kPeopleApps.title,
            toAuthoredCards(kPeopleApps, audienceState),
        );

        if (spliceGenSection === undefined && peopleSection === undefined) {
            scrollArea.appendChild(createAccessGate(openEmbeddedLink));
            return;
        }
        if (spliceGenSection !== undefined) {
            scrollArea.appendChild(spliceGenSection);
        }
        if (spliceGenSection !== undefined && peopleSection !== undefined) {
            scrollArea.appendChild(createEl("hr", styles.divider));
        }
        if (peopleSection !== undefined) {
            scrollArea.appendChild(peopleSection);
        }
    };

    audienceStateSignal.listenAndInit(update);

    return createEl("div", styles.container, [
        headerEl,
        scrollArea,
        createEl("div", styles.scrollFade),
    ]);
};
